/**
 * ProShip Direct v2 — the OTP step shown inside the activation modal.
 *
 * Second of two steps: the merchant has reviewed what they are about to be charged, and now proves
 * they hold the account before it happens. See data/activationOtp.js for the mechanics — and for
 * the standing warning that the code is generated client-side until backend provides the two
 * endpoints, so this gates a mistake rather than an attacker.
 *
 * WHAT THE UI OWES THE MERCHANT, in order of how often it is got wrong:
 *   - which inbox to open, masked but recognisable
 *   - how long the code lasts, counting down, not "expires soon"
 *   - how many attempts remain, before they are locked out rather than after
 *   - a resend that says when it will be available, not one that silently does nothing
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';

import { secondaryBtnSx } from '../../../styles/patterns';
import { SUBTLE, NAVY, BORDER, LIGHT_BG, WARNING, ERROR, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import {
  OTP_LENGTH, OTP_MESSAGES, maskEmail, secondsLeft, attemptsLeft, resendIn, MAX_ATTEMPTS,
} from '../data/activationOtp';

const bodySx = { fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.5 };
const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE };

const mmss = (total) => {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

export default function ActivationOtpStep({
  challenge,
  email,
  entered,
  onChange,
  error,
  sending,
  onResend,
  /*
    SERVER MODE. When the two OTP endpoints are wired (the VAS hub uses them today), the code is
    generated and checked on the server — so there is no client-side code to show, and the failure
    text comes from the server rather than a local reason enum. Default (unset) keeps the original
    client-side behaviour for callers not yet wired, so this stays additive.
  */
  serverMode = false,
  /* The server's own message for a failed verify, shown verbatim when serverMode is on. */
  errorText = '',
}) {
  /*
    A tick, purely so the countdown and the resend cooldown move.

    Time is read from the challenge on every render rather than kept in state — state would be a
    second copy of the same fact, and the two would disagree the moment a resend landed.
  */
  const [, tick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const left = secondsLeft(challenge);
  const tries = attemptsLeft(challenge);
  const cooldown = resendIn(challenge);
  const expired = left === 0;
  const locked = tries === 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pt: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <MarkEmailReadOutlinedIcon sx={{ fontSize: 20, color: PRIMARY_BLUE, mt: '2px' }} />
        <Box>
          <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>
            Confirm it is you
          </Typography>
          <Typography sx={bodySx}>
            We sent a {OTP_LENGTH}-digit code to{' '}
            <Box component='span' sx={{ color: NAVY, fontWeight: WEIGHT.semibold }}>
              {maskEmail(email)}
            </Box>
            . Activation starts a recurring charge, so it needs the code.
          </Typography>
        </Box>
      </Box>

      <TextField
        autoFocus
        size='small'
        value={entered}
        disabled={locked || expired}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
        placeholder={'•'.repeat(OTP_LENGTH)}
        inputProps={{
          inputMode: 'numeric',
          autoComplete: 'one-time-code',
          style: {
            fontFamily: FONT,
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: '0.5em',
            textAlign: 'center',
            color: NAVY,
          },
        }}
        sx={{ '& .MuiInputBase-root': { bgcolor: LIGHT_BG } }}
      />

      {/* Everything time-sensitive in one row, so nothing about the code's state is a surprise. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.md, flexWrap: 'wrap' }}>
        <Typography sx={{ ...smallSx, color: expired ? ERROR : SUBTLE }}>
          {expired ? 'Code expired' : `Expires in ${mmss(left)}`}
        </Typography>
        {!locked && tries < MAX_ATTEMPTS && (
          <Typography sx={{ ...smallSx, color: WARNING }}>
            {tries} attempt{tries === 1 ? '' : 's'} left
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {/* Locked is terminal for this attempt, so resend is not offered as a way out. */}
        <Button
          size='small'
          sx={secondaryBtnSx}
          disabled={locked || sending || (cooldown > 0 && !expired)}
          onClick={onResend}
          startIcon={sending ? <CircularProgress size={12} /> : null}
        >
          {sending ? 'Sending' : cooldown > 0 && !expired ? `Resend in ${cooldown}s` : 'Resend code'}
        </Button>
      </Box>

      {(serverMode ? Boolean(errorText) : Boolean(error)) && (
        <Alert severity={locked || expired ? 'warning' : 'error'} sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
          {serverMode ? errorText : (OTP_MESSAGES[error] || 'That code could not be verified.')}
        </Alert>
      )}

      {/*
        THE DEVELOPER STRIP, and it is not a design flourish — it is the honest consequence of there
        being no endpoint. The code is generated in this browser and no mail is sent, so without
        showing it the flow cannot be walked at all. It also makes the gap impossible to miss: the
        moment the two endpoints exist, this block and the client-side challenge go together.

        Hidden in serverMode: there a real code was mailed, so there is nothing to print and nothing
        to warn about.
      */}
      {!serverMode && (
        <Box
          sx={{
            p: 1,
            borderRadius: RADIUS.sm,
            border: `1px dashed ${WARNING}`,
            bgcolor: `${WARNING}0f`,
          }}
        >
          <Typography sx={{ ...smallSx, color: NAVY }}>
            <b>Not wired to email yet.</b> No mail is sent — there is no endpoint for an in-app OTP,
            so the code is generated in this browser and shown here to make the step usable:{' '}
            <Box component='span' sx={{ fontWeight: WEIGHT.bold, letterSpacing: '0.12em' }}>
              {challenge?.code || '—'}
            </Box>
          </Typography>
          <Typography sx={{ ...smallSx, mt: 0.4 }}>
            Until it is server-side this checks intent, not identity.
          </Typography>
        </Box>
      )}

      <Typography sx={{ ...smallSx, borderTop: `1px solid ${BORDER}`, pt: 0.85 }}>
        Not expecting this? Close this window — nothing is charged until the code is accepted.
      </Typography>
    </Box>
  );
}
